import React, { Fragment, useMemo, useCallback } from 'react';
import filter from 'lodash/filter';
import includes from 'lodash/includes';

import {
  IsFetched,
  ErrorMessage,
  FetchItemsLoadMoreItems
} from '../../../../../types';

import {
  FetchMaterialsCacheKeys,
  MaterialNanoID,
  FetchMaterialsFilters,
  ChangeMaterialsFiltersFunc,
  MaterialBrandID,
  FetchMaterialsLimit,
  FetchMaterialsPage,
  UpdateMaterialCacheAction
} from '../../../../materials/materialsTypes';
import {
  ThreeDStockMaterialsCompactListMaterial,
  ThreeDStockMaterialsCompactListMaterials
} from './ThreeDStockMaterialsCompactList.types';

import { ThreeDStockMaterialsCompactListItem } from '../ThreeDStockMaterialsCompactListItem';
import { ThreeDStockMaterialsListLightboxTitle } from '../ThreeDStockMaterialsList/components/ThreeDStockMaterialsListLightboxTitle';

import { AlertMessage } from '../../../../../helpers/AlertMessage';
import { Loading } from '../../../../../helpers/Loading';
import { NoResults } from '../../../../../helpers/NoResults';
import {
  LightboxWrapper,
  useLightboxWrapper,
  LightboxRenderImageTitle
} from '../../../../../helpers/LightboxWrapper';
import { LoadMoreButtonHelper } from '../../../../../helpers/buttons/LoadMoreButtonHelper';
import { LoadingSkeleton } from '../../../../../helpers/LoadingSkeleton';

import { Files } from '../../../../../utils/Files';

import { MaterialsPermissions } from '../../../../materials/materialsConstants';

type ThreeDStockMaterialsCompactListProps = {
  onMaterialEditButtonMouseEnter?: (materialNanoId: MaterialNanoID) => void;
  materials: ThreeDStockMaterialsCompactListMaterials;
  materialsError: ErrorMessage;
  materialsFetched: IsFetched;
  materialsIsPlaceholderData: boolean;
  materialsTotalCount: number;
  materialsFilters: FetchMaterialsFilters;
  changeMaterialsFilters: ChangeMaterialsFiltersFunc;
  hasNextMaterialsPage: boolean;
  materialsPage: FetchMaterialsPage;
  materialsLimit: FetchMaterialsLimit;
  loadMoreMaterials: FetchItemsLoadMoreItems;
  materialsFetchingNextPage: boolean;
  updateMaterialCache?: UpdateMaterialCacheAction<ThreeDStockMaterialsCompactListMaterial>;
};

function ThreeDStockMaterialsCompactList({
  onMaterialEditButtonMouseEnter,
  materials,
  materialsError,
  materialsFetched,
  materialsIsPlaceholderData,
  materialsTotalCount,
  materialsFilters,
  changeMaterialsFilters,
  hasNextMaterialsPage,
  materialsPage,
  materialsLimit,
  loadMoreMaterials,
  materialsFetchingNextPage,
  updateMaterialCache
}: ThreeDStockMaterialsCompactListProps) {
  const materialsInLightbox =
    useMemo<ThreeDStockMaterialsCompactListMaterials>(() => {
      return filter<ThreeDStockMaterialsCompactListMaterial>(
        materials,
        (material) => material.image?.file && Files.isImage(material.image.file)
      );
    }, [materials]);

  const handleMaterialBrandButtonClick = useCallback<
    (brandId: MaterialBrandID) => void
  >(
    (brandId) => {
      const currentBrandFilterIds = materialsFilters?.brandId?.in || [];

      if (!includes(currentBrandFilterIds, brandId)) {
        changeMaterialsFilters({
          brandId: { in: [...currentBrandFilterIds, brandId] }
        });
      }
    },
    [changeMaterialsFilters, materialsFilters?.brandId?.in]
  );

  const {
    handleLightboxClose,
    handleLightboxNext,
    handleLightboxOpen,
    handleLightboxOpenOnSlide,
    handleLightboxPrev,
    index,
    imagesCount,
    imageItem,
    lightBoxOpened,
    mainSrc,
    prevSrc,
    nextSrc
  } = useLightboxWrapper({
    items: materialsInLightbox
  });

  const renderImageTitle = useCallback<LightboxRenderImageTitle>(
    ({ index }) => {
      const material = materialsInLightbox[index];
      if (!material) {
        return null;
      }

      return <ThreeDStockMaterialsListLightboxTitle material={material} />;
    },
    [materialsInLightbox]
  );

  return (
    <Fragment>
      <AlertMessage addClassName="m-4" message={materialsError} />
      <LoadingSkeleton loaded={materialsIsPlaceholderData || materialsFetched}>
        {materialsTotalCount === 0 && <NoResults addErrorClassName="m-4" />}
        {materialsTotalCount > 0 && (
          <div className="min-w-0 -mx-2 space-y-1">
            <div className="flex justify-center items-center">
              <Loading loaded={!materialsIsPlaceholderData} />
            </div>

            {materials.map((material) => (
              <ThreeDStockMaterialsCompactListItem
                key={material.uuid}
                material={material}
                onMaterialEditButtonMouseEnter={onMaterialEditButtonMouseEnter}
                onMaterialBrandButtonClick={handleMaterialBrandButtonClick}
                onLightboxOpen={handleLightboxOpenOnSlide}
                updateMaterialCache={updateMaterialCache}
              />
            ))}

            {hasNextMaterialsPage && (
              <LoadMoreButtonHelper
                action={
                  MaterialsPermissions.READ_MATERIALS_LOAD_MORE_BUTTON_ITEMS_COUNT
                }
                dataGa="materials-load-more-button"
                itemsTotalCount={materialsTotalCount}
                page={materialsPage}
                limit={materialsLimit}
                loadMoreItems={loadMoreMaterials}
                itemsFetchingNextPage={materialsFetchingNextPage}
              />
            )}
          </div>
        )}
      </LoadingSkeleton>
      <LightboxWrapper
        handleLightboxClose={handleLightboxClose}
        handleLightboxNext={handleLightboxNext}
        handleLightboxOpen={handleLightboxOpen}
        handleLightboxPrev={handleLightboxPrev}
        index={index}
        imagesCount={imagesCount}
        imageItem={imageItem}
        lightBoxOpened={lightBoxOpened}
        mainSrc={mainSrc}
        nextSrc={nextSrc}
        prevSrc={prevSrc}
        renderImageTitle={renderImageTitle}
        // renderCustomButtons={renderLightboxButtons}
        withFullScreenButton
      />
    </Fragment>
  );
}

export default ThreeDStockMaterialsCompactList;
